import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { Icon } from '@ibk/shared-ui';
import { formatShortDateWithoutTimezone } from '@ibk/shared-utils';
import { beneficiosTranslations } from '../../translations';
import styles from './BeneficiaryDetailsTooltip.module.css';
function BeneficiaryDetailsTooltipContent({ beneficiary, testIdIdentifier = 'BeneficiaryDetailsTooltip', showContractType = false, }) {
    const { t } = useTranslation();
    const formatValue = (value) => {
        return value ?? '-';
    };
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);
    const [isTooltipOnLeft, setIsTooltipOnLeft] = useState(false);
    const hideTimeoutRef = useRef(null);
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current)
            return;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const spacing = 8;
        const arrowWidth = 8;
        let top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        let left = triggerRect.right + spacing + arrowWidth;
        let isLeft = false;
        if (left + tooltipRect.width > viewportWidth - spacing) {
            left = triggerRect.left - tooltipRect.width - spacing - arrowWidth;
            isLeft = true;
        }
        if (top < spacing) {
            top = spacing;
        }
        else if (top + tooltipRect.height > viewportHeight - spacing) {
            top = viewportHeight - tooltipRect.height - spacing;
        }
        if (left < spacing) {
            left = spacing;
        }
        setTooltipPosition({ top, left });
        setIsTooltipOnLeft(isLeft);
        setIsPositioned(true);
    }, []);
    useEffect(() => {
        if (isVisible) {
            calculatePosition();
            const handleResize = () => calculatePosition();
            const handleScroll = () => calculatePosition();
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isVisible, calculatePosition]);
    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);
    const showTooltip = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setIsVisible(true);
        setIsPositioned(false);
    }, []);
    const hideTooltip = useCallback(() => {
        setIsVisible(false);
        setIsPositioned(false);
    }, []);
    const handleMouseLeave = useCallback(() => {
        hideTimeoutRef.current = setTimeout(() => {
            if (hideTimeoutRef.current) {
                setIsVisible(false);
                setIsPositioned(false);
                hideTimeoutRef.current = null;
            }
        }, 300);
    }, []);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!isVisible) {
                showTooltip();
            }
        }
        else if (event.key === 'Escape' && isVisible) {
            event.preventDefault();
            hideTooltip();
        }
    }, [isVisible, showTooltip, hideTooltip]);
    const handleClick = useCallback(() => {
        if (!isVisible) {
            showTooltip();
        }
    }, [isVisible, showTooltip]);
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: triggerRef, type: 'button', className: styles.iconTrigger, "data-testid": `view${testIdIdentifier}IconTrigger`, "aria-label": t('beneficiary.details.information'), "aria-expanded": isVisible, onMouseEnter: showTooltip, onMouseLeave: handleMouseLeave, onClick: handleClick, onKeyDown: handleKeyDown, children: _jsx(Icon, { name: 'actionInformationDark', width: 16, height: 16 }) }), isVisible && (_jsxs("div", { ref: tooltipRef, className: `${styles.tooltip} ${isTooltipOnLeft ? styles.tooltipLeft : styles.tooltipRight}`, style: {
                    position: 'fixed',
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    opacity: isPositioned ? 1 : 0.01,
                    visibility: 'visible',
                    zIndex: 9999,
                }, "data-testid": `view${testIdIdentifier}Content`, role: 'tooltip', "aria-hidden": !isVisible, onMouseEnter: () => {
                    if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current);
                        hideTimeoutRef.current = null;
                    }
                }, onMouseLeave: handleMouseLeave, children: [_jsx("div", { className: styles.tooltipContent, children: _jsxs("div", { className: styles.tooltipSection, children: [showContractType && (_jsxs("div", { className: styles.tooltipRow, "data-testid": `textView${testIdIdentifier}Type`, children: [t('beneficiary.details.type'), ": ", formatValue(beneficiary.contractType)] })), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.registrationNumber'), ": ", formatValue(beneficiary.employeeCode)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.admission'), ":", ' ', beneficiary.hireDate ? formatShortDateWithoutTimezone(beneficiary.hireDate) : '-'] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.jobRole'), ": ", formatValue(beneficiary.jobRole)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.costCenter'), ": ", formatValue(beneficiary.costCenter)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.department'), ": ", formatValue(beneficiary.department)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.category'), ": ", formatValue(beneficiary.category)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.employmentBond'), ": ", formatValue(beneficiary.employmentBond)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.branch'), ": ", formatValue(beneficiary.branch)] }), _jsxs("div", { className: styles.tooltipRow, children: [t('beneficiary.details.union'), ": ", formatValue(beneficiary.union)] })] }) }), _jsx("div", { className: `${styles.arrow} ${isTooltipOnLeft ? styles.arrowLeft : styles.arrowRight}` })] }))] }));
}
export function BeneficiaryDetailsTooltip(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(BeneficiaryDetailsTooltipContent, { ...props }) }));
}
